// Timeline consolidation — turns raw ARRIVAL/DEPARTURE event arrays into
// merged "stops" interleaved with driving segments.  Pure functions, zero
// React deps.  Mirrors the gpsUtils.js pattern in the same folder.

const MERGE_GAP_MINUTES = 5;
const ASPIRE_MARKER_LOCATION_TYPES = new Set([
  'CLOCK_IN',
  'CLOCK_OUT',
  'JOB_CHECK_IN',
  'JOB_CHECK_OUT',
  'SCHEDULED_ARRIVAL',
  'SCHEDULED_DEPARTURE',
  'WORK_TICKET_COMPLETED',
]);

/**
 * Build a human-friendly display name from 1+ location names.
 */
const buildDisplayName = (names) => {
  const unique = [...new Set(names.filter(Boolean))];
  if (unique.length === 0) return 'Unknown';
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} + ${unique[1]}`;
  return `${unique[0]} + ${unique.length - 1} others`;
};

/**
 * Normalise locationType strings coming from the API into the canonical
 * bucket used for colouring / icons.
 */
const normalizeLocationType = (raw) => {
  if (!raw) return 'OTHER';
  const upper = raw.toUpperCase().replace(/\s+/g, '_');
  if (upper === 'ON_SITE' || upper === 'JOBSITE') return 'JOBSITE';
  if (upper === 'TRACKED_LOCATION' || upper === 'AT_TRACKED_LOCATION') return 'TRACKED_LOCATION';
  if (upper === 'YARD' || upper === 'AT_YARD') return 'YARD';
  if (upper === 'IDLE_OTHER' || upper === 'OTHER') return 'IDLE_OTHER';
  return upper;
};

/**
 * Calculate duration in minutes between two date-like values.
 */
const diffMinutes = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms > 0 ? Math.round(ms / 60000) : 0;
};

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const uniqueStrings = (values = []) => {
  return [...new Set(
    values
      .map((value) => (value == null ? '' : `${value}`.trim()))
      .filter(Boolean),
  )];
};

const extractStopProximity = (rawEvents = []) => {
  for (const ev of rawEvents) {
    const distanceFromLocationMeters = toFiniteNumber(ev?.distanceFromLocationMeters);
    const geofenceRadiusMeters = toFiniteNumber(ev?.geofenceRadiusMeters);
    const distanceToGeofenceMeters = toFiniteNumber(ev?.distanceToGeofenceMeters);

    if (
      distanceFromLocationMeters !== null ||
      geofenceRadiusMeters !== null ||
      distanceToGeofenceMeters !== null
    ) {
      return {
        distanceFromLocationMeters,
        geofenceRadiusMeters,
        distanceToGeofenceMeters,
      };
    }
  }

  return {
    distanceFromLocationMeters: null,
    geofenceRadiusMeters: null,
    distanceToGeofenceMeters: null,
  };
};

const isMarkerEvent = (event) => {
  if (!event) return false;
  if (event.type === 'MARKER') return true;
  if (event.markerType) return true;
  const normalized = normalizeLocationType(event.locationType);
  return ASPIRE_MARKER_LOCATION_TYPES.has(normalized);
};

/**
 * consolidateTimelineEvents(events) → TimelineEntry[]
 *
 * @param {Array} events — raw timeline events from the API, each having at
 *   minimum { type:'ARRIVAL'|'DEPARTURE', time, locationType, locationName }
 *
 * @returns {Array<{ kind:'STOP'|'DRIVING', startTime, endTime, durationMinutes, stop:Object|null }>}
 */
export const consolidateTimelineEvents = (events) => {
  if (!Array.isArray(events) || events.length === 0) return [];

  // 1. Sort by time ascending
  const sorted = [...events].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
  );

  const markerEntries = sorted
    .filter((event) => isMarkerEvent(event))
    .map((event) => {
      const markerType = normalizeLocationType(event.markerType || event.locationType || 'ASPIRE_EVENT');
      return {
        kind: 'MARKER',
        time: event.time,
        marker: {
          markerType,
          locationName: event.locationName || 'Aspire Event',
          lat: event.lat ?? null,
          lon: event.lon ?? null,
          source: event.source || null,
          matchedBy: event.matchedBy || null,
          contactId: event.contactId ?? null,
          employeeId: event.employeeId ?? null,
          crewId: event.crewId ?? null,
        },
      };
    });

  // 2. Pair each ARRIVAL with the very next DEPARTURE (sequential pairing).
  //    Real-world visits are strictly sequential: arrive → depart → arrive …
  //    so we don't need to match by name/type — the next DEPARTURE always
  //    closes the current stop.
  const rawStops = [];
  const usedDepartures = new Set();

  for (let i = 0; i < sorted.length; i++) {
    const ev = sorted[i];
    if (ev.type !== 'ARRIVAL') continue;
    if (isMarkerEvent(ev)) continue;

    const normType = normalizeLocationType(ev.locationType);
    const arrivalName = ev.locationName || '';

    // Grab the next unused DEPARTURE after this ARRIVAL.
    // If we encounter another ARRIVAL first, this stop has no departure.
    let departureEvent = null;
    for (let j = i + 1; j < sorted.length; j++) {
      const candidate = sorted[j];
      if (candidate.type === 'ARRIVAL') break; // next visit started — no departure for ours
      if (isMarkerEvent(candidate)) continue;
      if (candidate.type === 'DEPARTURE' && !usedDepartures.has(j)) {
        departureEvent = candidate;
        usedDepartures.add(j);
        break;
      }
    }

    const arrivalTime = ev.time;
    const departureTime = departureEvent?.time || null;
    const duration = departureTime
      ? diffMinutes(arrivalTime, departureTime)
      : (ev.visitDuration != null ? Math.round(Number(ev.visitDuration)) : null);

    rawStops.push({
      arrivalTime,
      departureTime,
      durationMinutes: duration,
      locationType: normType,
      locationName: arrivalName,
      locations: [arrivalName],
      rawEvents: departureEvent ? [ev, departureEvent] : [ev],
      ...extractStopProximity(departureEvent ? [ev, departureEvent] : [ev]),
      lat: ev.lat ?? null,
      lon: ev.lon ?? null,
      isActive: false, // set below — only the last stop can be active
    });
  }

  // Only the very last stop (chronologically) can be "currently here"
  if (rawStops.length > 0) {
    const last = rawStops[rawStops.length - 1];
    if (!last.departureTime) {
      last.isActive = true;
    }
  }

  if (rawStops.length === 0) return markerEntries;

  // 3. Merge adjacent stops of the same locationType when gap < MERGE_GAP_MINUTES
  const merged = [rawStops[0]];

  for (let i = 1; i < rawStops.length; i++) {
    const prev = merged[merged.length - 1];
    const curr = rawStops[i];

    const prevEnd = prev.departureTime || prev.arrivalTime;
    const gapMinutes = diffMinutes(prevEnd, curr.arrivalTime);

    if (
      prev.locationType === curr.locationType &&
      gapMinutes !== null &&
      gapMinutes < MERGE_GAP_MINUTES
    ) {
      // Merge into prev
      prev.departureTime = curr.departureTime || prev.departureTime;
      prev.isActive = prev.isActive || curr.isActive;
      prev.rawEvents = prev.rawEvents.concat(curr.rawEvents);
      Object.assign(prev, extractStopProximity(prev.rawEvents));
      if (curr.locationName && !prev.locations.includes(curr.locationName)) {
        prev.locations.push(curr.locationName);
      }
      // Recalculate duration across merged span
      if (prev.departureTime) {
        prev.durationMinutes = diffMinutes(prev.arrivalTime, prev.departureTime);
      } else if (prev.durationMinutes != null && curr.durationMinutes != null) {
        prev.durationMinutes += (gapMinutes || 0) + curr.durationMinutes;
      }
    } else {
      merged.push(curr);
    }
  }

  // Finalise display names after merge
  merged.forEach((stop) => {
    stop.displayName = buildDisplayName(stop.locations);
    stop.sources = uniqueStrings(stop.rawEvents.map((event) => event?.source));
    stop.matchedBy = uniqueStrings(stop.rawEvents.map((event) => event?.matchedBy));
    stop.hasIdlingEvidence = stop.rawEvents.some((event) => {
      if (!event) return false;
      if (event.idlingMatched) return true;
      if (event.source === 'IDLING_EVENT') return true;
      return toFiniteNumber(event.idlingDurationMinutes) !== null;
    });
    const idlingDurations = stop.rawEvents
      .map((event) => toFiniteNumber(event?.idlingDurationMinutes))
      .filter((value) => value !== null);
    stop.idlingDurationMinutes = idlingDurations.length > 0
      ? Math.max(...idlingDurations)
      : null;
  });

  // 4. Interleave DRIVING entries between stops
  const timeline = [];

  for (let i = 0; i < merged.length; i++) {
    const stop = merged[i];

    // Add driving segment before this stop (between previous stop's departure and this arrival)
    if (i > 0) {
      const prevStop = merged[i - 1];
      const drivingStart = prevStop.departureTime || prevStop.arrivalTime;
      const drivingEnd = stop.arrivalTime;
      const drivingDuration = diffMinutes(drivingStart, drivingEnd);

      if (drivingDuration && drivingDuration > 0) {
        timeline.push({
          kind: 'DRIVING',
          startTime: drivingStart,
          endTime: drivingEnd,
          durationMinutes: drivingDuration,
          stop: null,
        });
      }
    }

    timeline.push({
      kind: 'STOP',
      startTime: stop.arrivalTime,
      endTime: stop.departureTime,
      durationMinutes: stop.durationMinutes,
      stop,
    });
  }

  if (markerEntries.length > 0) {
    timeline.push(...markerEntries);
    timeline.sort((a, b) => {
      const aTime = new Date(a.startTime || a.time).getTime();
      const bTime = new Date(b.startTime || b.time).getTime();
      return aTime - bTime;
    });
  }

  return timeline;
};

export { normalizeLocationType, buildDisplayName, diffMinutes };
